/**
 * 606 Meta5 AI Trading - API Client
 */
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/trading`;

const api = axios.create({
  baseURL: API_URL,
  timeout: 25000,
});

// Add auth token to requests
// AuthContext stores the JWT in sessionStorage under 'admin_token'.
// We fall back through legacy keys for robustness across older code paths.
api.interceptors.request.use((config) => {
  const token =
    sessionStorage.getItem('admin_token') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Account
export const fetchAccount = () => api.get('/account/info').then(res => res.data);

// Positions
export const fetchPositionsOpen = () => api.get('/positions/open').then(res => res.data);
export const fetchPositionsHistory = (limit = 100) => api.get(`/positions/history?limit=${limit}`).then(res => res.data);
export const closePosition = (ticket) => api.post('/positions/close', { ticket }).then(res => res.data);

// Bot Control
export const startBot = () => api.post('/bot/start').then(res => res.data);
export const stopBot = () => api.post('/bot/stop').then(res => res.data);
export const fetchBotStatus = () => api.get('/bot/status').then(res => res.data);
export const fetchSettings = () => api.get('/bot/settings').then(res => res.data);
export const updateSettings = (data) => api.put('/bot/settings', data).then(res => res.data);

// News & Calendar
export const fetchNews = (limit = 20) => api.get(`/news/latest?limit=${limit}`).then(res => res.data);
export const refreshNews = () => api.post('/news/refresh').then(res => res.data);
export const refreshNewsAI = () => api.post('/news/refresh-ai').then(res => res.data);
export const fetchCalendar = (days = 7) => api.get(`/news/calendar?days=${days}`).then(res => res.data);

// AI Logs
export const fetchAILogs = (limit = 100, level = null) => {
  const params = new URLSearchParams({ limit });
  if (level) params.append('level', level);
  return api.get(`/ai/logs?${params}`).then(res => res.data);
};

// Analytics
export const fetchPerformance = () => api.get('/analytics/performance').then(res => res.data);
export const fetchStrategyPerformance = () => api.get('/analytics/by-strategy').then(res => res.data);

// MT5
export const mt5Connect = (data) => api.post('/mt5/connect', data).then(res => res.data);
export const mt5Disconnect = () => api.post('/mt5/disconnect').then(res => res.data);
export const mt5Status = () => api.get('/mt5/status').then(res => res.data);
export const mt5ListAccounts = () => api.get('/mt5/list-accounts').then(res => res.data);
export const mt5CreateAccount = (data) => api.post('/mt5/create-account', data).then(res => res.data);
// Permanently delete a remote MetaApi account (frees its resource slots on the plan)
export const mt5DeleteRemoteAccount = (accountId) =>
  api.delete(`/mt5/remote-accounts/${accountId}`).then(res => res.data);

// MT5 multi-account storage (local: save / list / switch / delete)
export const mt5SavedAccounts = () => api.get('/mt5/saved-accounts').then(res => res.data);
export const mt5SaveAccount = (data) => api.post('/mt5/saved-accounts', data).then(res => res.data);
export const mt5SwitchAccount = (id) => api.post(`/mt5/saved-accounts/${id}/switch`).then(res => res.data);
export const mt5DeleteSavedAccount = (id) => api.delete(`/mt5/saved-accounts/${id}`).then(res => res.data);
export const mt5UpdateRotation = (id, data) => api.put(`/mt5/saved-accounts/${id}/rotation`, data).then(res => res.data);
export const mt5RefreshAccountStats = (id) => api.post(`/mt5/saved-accounts/${id}/refresh-stats`).then(res => res.data);
export const mt5AutoRotateCheck = () => api.post('/mt5/auto-rotate/check').then(res => res.data);
export const mt5RotationLog = (limit = 20) => api.get(`/mt5/rotation-log?limit=${limit}`).then(res => res.data);
export const fetchSmcAnalysis = (symbol = 'XAUUSD', timeframe = '15m') =>
  api.get(`/analysis/smc?symbol=${symbol}&timeframe=${timeframe}`).then(res => res.data);
export const fetchSymbols = () => api.get('/mt5/symbols').then(res => res.data);
export const fetchPrices = () => api.get('/mt5/prices').then(res => res.data);

// Backtest
export const runBacktest = (params) => api.post('/backtest/run', params).then(res => res.data);

// Trading Schools (المدارس التداولية)
export const fetchSchools = () => api.get('/schools').then(res => res.data);
export const fetchSchoolDetail = (id) => api.get(`/schools/${id}`).then(res => res.data);
export const applySchool = (id) => api.post(`/schools/${id}/apply`).then(res => res.data);
export const fetchSchoolAnalytics = (days = 30) => api.get(`/schools/analytics?days=${days}`).then(res => res.data);

// MetaApi Profiles (مفاتيح MetaApi)
export const fetchMetaApiProfiles = () => api.get('/metaapi/profiles').then(res => res.data);
export const createMetaApiProfile = (data) => api.post('/metaapi/profiles', data).then(res => res.data);
export const updateMetaApiProfile = (id, data) => api.put(`/metaapi/profiles/${id}`, data).then(res => res.data);
export const deleteMetaApiProfile = (id) => api.delete(`/metaapi/profiles/${id}`).then(res => res.data);
export const activateMetaApiProfile = (id) => api.post(`/metaapi/profiles/${id}/activate`).then(res => res.data);

export default api;
